/**
 * src/hooks/useAppearance.ts
 * ----------------------------
 * React hook for loading and reading the org appearance configuration.
 *
 * Fully implemented this will:
 *   - Expose loadAppearance(orgId) delegating to the appearance store.
 *   - Expose config and isLoaded from the store for use in components.
 *   - Provide voiceEnabled, allowAnonymous, welcomeMessage convenience
 *     accessors with safe defaults.
 *
 * Key dependencies:
 *   src/stores/appearance.ts
 */
import { useAppearanceStore } from '@/stores/appearance'
import type { AppearanceConfig } from '@/api/appearance'

export function useAppearance() {
  const config = useAppearanceStore((state) => state.config)
  const isLoaded = useAppearanceStore((state) => state.isLoaded)
  const loadAppearance = useAppearanceStore((state) => state.loadAppearance)

  const safeConfig: Partial<AppearanceConfig> = config ?? {}

  return {
    config,
    isLoaded,
    loadAppearance,
    voiceEnabled:    safeConfig.voiceEnabled    ?? true,
    allowAnonymous:  safeConfig.allowAnonymous  ?? true,
    welcomeMessage:  safeConfig.welcomeMessage  ?? 'Hi! How can I help you today?',
    orgName:         safeConfig.orgName         ?? 'Helix',
    agentAvatarUrl:  safeConfig.agentAvatarUrl  ?? null,
    logoUrl:         safeConfig.logoUrl         ?? null,
  }
}
